import React from 'react';
import { trackEvent, BOOKING_SCREENING_URL, EMAIL_ADDRESS } from '../lib/tracking';
import { optimizeImage } from '../lib/image';

export default function Workshops() {
  const topics = [
    {
      title: 'Værdiafklaring',
      desc: 'Skab klarhed og strategisk retning, der styrker beslutningsprocesser, motivation og medarbejdertrivsel i hverdagen.',
    },
    {
      title: 'Assertiv kommunikation',
      desc: 'Tydelig og respektfuld dialog. Lær at sige din mening konstruktivt, sætte sunde grænser og håndtere svære samtaler.',
    },
    {
      title: 'Coachingteknikker for ledere',
      desc: 'Praktiske metoder og spørgeteknikker til at motivere, engagere, sparre med og fastholde dine medarbejdere.',
    },
    {
      title: 'Effektiv kommunikation',
      desc: 'Kommunikation er nøglen til lederskab. Få budskabet klart igennem og reducer misforståelser på tværs af organisationen.',
    },
    {
      title: 'Konflikthåndtering',
      desc: 'Lær at identificere, adressere og omdanne uoverensstemmelser og konflikter til gensidig udvikling og stærkere samarbejde.',
    },
    {
      title: 'Motivation & potentiale',
      desc: 'Indsigt i psykologiske motivationsfaktorer og konkrete strategier, der vækker teamets fulde potentiale og trivsel.',
    },
  ];

  return (
    <div className="bg-[#ECEEF1]">
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block">
                Erhverv · Inspiration & Læring
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#5A232E] tracking-tight leading-[1.12]">
                Foredrag & workshops
              </h1>
              <p className="text-base sm:text-lg text-[#6c7480] leading-relaxed">
                Praksisnære og engagerende arrangementer med konkrete værktøjer for ledere og medarbejdere. Med fokus på hjernen, relationer, kommunikation og bæredygtig forandring.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={`mailto:${EMAIL_ADDRESS}?subject=Forespørgsel på foredrag eller workshop`}
                  onClick={() => trackEvent('cta_booking_click', { location: 'workshops_hero' })}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all no-break whitespace-nowrap"
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  <span>Indhent uforpligtende tilbud</span>
                </a>
                <a
                  href={BOOKING_SCREENING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#ECEEF1] text-[#5A232E] font-medium text-sm border border-[#D9DDE2] hover:bg-[#D9DDE2] transition-colors no-break whitespace-nowrap"
                >
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <span>Book afklaringssamtale</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-[#D9DDE2]">
                <img
                  src={optimizeImage('kim-outdoor-park-2.webp', 800)}
                  alt="Kim Palasz - Foredrag og workshops"
                  className="w-full h-auto object-cover max-h-[440px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emneoversigt */}
      <section className="py-20 sm:py-24 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
              Temaer & Indhold
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
              Oplæg og workshops tilpasset jeres behov
            </h2>
            <p className="text-sm sm:text-base text-[#6c7480] mt-3 leading-relaxed">
              Alle arrangementer skræddersys til virksomhedens eller organisationens konkrete hverdag og målgruppe:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topics.map((topic, i) => (
              <div key={i} className="bg-white p-7 rounded-2xl border border-[#D9DDE2] shadow-2xs space-y-3">
                <div className="w-9 h-9 rounded-xl bg-[#5A232E]/10 text-[#5A232E] flex items-center justify-center text-xs">
                  <i className="fa-solid fa-check" aria-hidden="true" />
                </div>
                <h3 className="font-display text-lg text-[#5A232E]">{topic.title}</h3>
                <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">{topic.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-20 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
            Vil I høre mere om et oplæg eller en workshop?
          </h2>
          <p className="text-sm sm:text-base text-[#6c7480] max-w-xl mx-auto leading-relaxed">
            Kontakt mig for en uforpligtende drøftelse af jeres ønsker til varighed, deltagere og fokusområder.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <a
              href={`mailto:${EMAIL_ADDRESS}?subject=Forespørgsel på workshop eller foredrag`}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all no-break whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              <span>Indhent uforpligtende tilbud på workshop</span>
            </a>
            <a
              href={BOOKING_SCREENING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-[#ECEEF1] hover:bg-[#D9DDE2] text-[#5A232E] font-medium text-sm border border-[#D9DDE2] transition-colors no-break whitespace-nowrap"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Book gratis samtale (20 min.)</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
